import { translateDbValue } from '@/utils/dbI18n'

/**
 * Статусы записей (`record_stages`).
 *
 * id фиксированы сидом бэка (`back/model/initializators/record_stages.init.js`),
 * поэтому логика сравнивает статусы по id, а не по названию: в БД `name` хранит
 * ключ перевода (`interface.record_stages.*`), а перевод — в `/locales`.
 * Пользовательский текст (данные до миграции) отображается как есть.
 */

export const RECORD_STAGE_IDS = Object.freeze({
  unconfirmed: 1,
  confirmed: 2,
  inProgress: 3,
  completed: 4,
  cancelled: 5,
  arrived: 6,
  checkConducted: 7,
})

/** Порядок статусов в интерфейсе (id в БД не трогаем). */
export const RECORD_STAGE_DISPLAY_ORDER = Object.freeze([
  RECORD_STAGE_IDS.unconfirmed,
  RECORD_STAGE_IDS.confirmed,
  RECORD_STAGE_IDS.arrived,
  RECORD_STAGE_IDS.inProgress,
  RECORD_STAGE_IDS.completed,
  RECORD_STAGE_IDS.checkConducted,
  RECORD_STAGE_IDS.cancelled,
])

/** Имя иконки статуса по его id. */
const RECORD_STAGE_ICONS = Object.freeze({
  [RECORD_STAGE_IDS.unconfirmed]: 'unconfirmed',
  [RECORD_STAGE_IDS.confirmed]: 'confirmed',
  [RECORD_STAGE_IDS.arrived]: 'arrived',
  [RECORD_STAGE_IDS.inProgress]: 'in-progress',
  [RECORD_STAGE_IDS.completed]: 'completed',
  [RECORD_STAGE_IDS.checkConducted]: 'completed',
  [RECORD_STAGE_IDS.cancelled]: 'cancelled',
})

/**
 * id статуса из объекта статуса или числа.
 * @param {Object|number|null} stage
 * @returns {number|null}
 */
export function getRecordStageId(stage) {
  if (stage == null) return null

  const id = Number(typeof stage === 'object' ? stage.id : stage)

  return Number.isFinite(id) ? id : null
}

/**
 * Название статуса: ключ из БД переводим, пользовательский текст оставляем.
 * @param {Object} stage
 * @param {Object} [i18n]
 * @returns {string}
 */
export function getRecordStageName(stage, i18n) {
  return translateDbValue(stage?.name, { i18n })
}

/** Иконка статуса по id. */
export function getRecordStageIcon(stage) {
  return RECORD_STAGE_ICONS[getRecordStageId(stage)] || 'unconfirmed'
}

export function isUnconfirmedStage(stage) {
  return getRecordStageId(stage) === RECORD_STAGE_IDS.unconfirmed
}

export function isConfirmedStage(stage) {
  return getRecordStageId(stage) === RECORD_STAGE_IDS.confirmed
}

export function isArrivedStage(stage) {
  return getRecordStageId(stage) === RECORD_STAGE_IDS.arrived
}

export function isInProgressStage(stage) {
  return getRecordStageId(stage) === RECORD_STAGE_IDS.inProgress
}

export function isCompletedStage(stage) {
  return getRecordStageId(stage) === RECORD_STAGE_IDS.completed
}

export function isCheckConductedStage(stage) {
  return getRecordStageId(stage) === RECORD_STAGE_IDS.checkConducted
}

export function isCancelledStage(stage) {
  return getRecordStageId(stage) === RECORD_STAGE_IDS.cancelled
}

/**
 * Красная полоса на карточке: ККМ включена, чека нет, визит уже закрыт.
 * Статус «Проведён чек» сам по себе чек не заменяет.
 */
export function missingReceiptLine(
  stage,
  { kkmEnabled = false, hasReceipt = false } = {}
) {
  if (!kkmEnabled || hasReceipt) return false

  return isClosedStage(stage)
}

/** Визит состоялся: «Завершена» или «Проведён чек». */
export function isClosedStage(stage) {
  const id = getRecordStageId(stage)

  return (
    id === RECORD_STAGE_IDS.completed || id === RECORD_STAGE_IDS.checkConducted
  )
}

export function getVisitHistoryMark(stage) {
  if (isCancelledStage(stage)) return 'cancelled'
  if (isClosedStage(stage)) return 'completed'
  return 'upcoming'
}

/** Статусы в порядке отображения; неизвестные — в конце. */
export function sortRecordStages(stages) {
  const order = RECORD_STAGE_DISPLAY_ORDER

  return [...(stages || [])].sort((a, b) => {
    const aIndex = order.indexOf(getRecordStageId(a))
    const bIndex = order.indexOf(getRecordStageId(b))

    return (
      (aIndex === -1 ? order.length : aIndex) -
      (bIndex === -1 ? order.length : bIndex)
    )
  })
}

export default {
  RECORD_STAGE_IDS,
  RECORD_STAGE_DISPLAY_ORDER,
  getRecordStageId,
  getRecordStageName,
  getRecordStageIcon,
  isUnconfirmedStage,
  isConfirmedStage,
  isArrivedStage,
  isInProgressStage,
  isCompletedStage,
  isCheckConductedStage,
  isCancelledStage,
  isClosedStage,
  getVisitHistoryMark,
  sortRecordStages,
}
